import React from "react";
import { AlertTriangle, Clock, FileUp } from "lucide-react";

import Button from "src/components/app/Button";
import {
  MILESTONE_DOC_STATE,
  getMilestoneDocCopy,
} from "src/constants/milestones";
import { Milestone } from "src/types/program.types";

// The documents gate for a milestone: what's needed, where it stands and the
// one action that moves it forward. Rendered as a footer strip inside the
// milestone card on the program details screen, and as its own card on the
// home screen — hence the variant.
const MilestoneDocumentsPrompt: React.FC<{
  milestone: Milestone;
  variant?: "footer" | "card";
  onAction: () => void;
}> = ({ milestone, variant = "footer", onAction }) => {
  const { docState, title, description, buttonLabel } =
    getMilestoneDocCopy(milestone);
  const isRejected = docState === MILESTONE_DOC_STATE.REJECTED;
  const isUnderReview = docState === MILESTONE_DOC_STATE.UNDER_REVIEW;

  const accentColor = isRejected
    ? "var(--status-error)"
    : isUnderReview
      ? "var(--status-warning)"
      : "var(--app-primary)";

  const Icon = isRejected ? AlertTriangle : isUnderReview ? Clock : FileUp;

  return (
    <div
      className={variant === "card" ? "rounded-xl p-3" : "px-3 py-3"}
      style={
        variant === "card"
          ? {
              backgroundColor: "var(--bg-secondary)",
              border: "1px solid var(--card-border)",
            }
          : {
              borderTop: "1px solid var(--card-border)",
              backgroundColor: "var(--bg-secondary)",
            }
      }
    >
      <div className="flex items-center gap-2">
        <Icon
          className="flex-shrink-0 w-4 h-4"
          style={{ color: accentColor }}
        />
        <h4
          className="text-sm font-semibold"
          style={{ color: "var(--card-title-color)" }}
        >
          {title}
        </h4>
      </div>

      <p className="text-sm mt-1" style={{ color: "var(--card-desc-color)" }}>
        {description}
      </p>

      <div className="mt-3">
        <Button
          size="sm"
          color="primary"
          variant={isUnderReview ? "outlined" : "contained"}
          onClick={onAction}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};

export default MilestoneDocumentsPrompt;
